// src/components/CalendarPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { fetchExternalCalendarEventsCallable } from '../services/firebase'; 

// --- ICONS ---
const ChevronLeft = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m15 18-6-6 6-6"/></svg> );
const ChevronRight = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m9 18 6-6-6-6"/></svg> );
const CalendarIcon = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg> );
const Clock = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> );
const MapPin = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg> );
const LinkIcon = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> );
const CopyIcon = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg> );
const ExternalLinkIcon = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg> );
const ImportIcon = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"/><path d="M12 12v9"/><path d="m16 16-4-4-4 4"/></svg> );
const TrashIcon = ({ size = 16, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg> );
const CheckSquare = ({ size = 24, className = "" }) => ( <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> );

// --- KONSTANTEN ---
const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

// Farben für importierte Kalender
const IMPORT_COLORS = [
    { bg: 'bg-purple-500', bgLight: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', darkBg: 'dark:bg-purple-900/20', darkText: 'dark:text-purple-300' },
    { bg: 'bg-pink-500', bgLight: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200', darkBg: 'dark:bg-pink-900/20', darkText: 'dark:text-pink-300' },
    { bg: 'bg-orange-500', bgLight: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', darkBg: 'dark:bg-orange-900/20', darkText: 'dark:text-orange-300' },
    { bg: 'bg-teal-500', bgLight: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200', darkBg: 'dark:bg-teal-900/20', darkText: 'dark:text-teal-300' },
];

// FÜGT tasks und onSelectTask ZU DEN PROPS HINZU
export default function CalendarPage({ meetings = [], externalMeetings = [], tasks = [], onSelectMeeting, onSelectTask, userId }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month');
  
  // Modals
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Import State (Array von Kalendern)
  const [importedCalendars, setImportedCalendars] = useState([]);
  
  // Inputs für neuen Import
  const [newImportUrl, setNewImportUrl] = useState('');
  const [newImportName, setNewImportName] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // States für die Abo Links
  const [subscribeUrlHttps, setSubscribeUrlHttps] = useState('');
  const [subscribeUrlWebcal, setSubscribeUrlWebcal] = useState('');

  // 1. Abo-URL generieren
  useEffect(() => {
      if (userId) {
          const projectId = 'meetform25'; 
          const region = 'europe-west1';
          const baseUrl = `${region}-${projectId}.cloudfunctions.net/getCalendarFeed?userId=${userId}`;
          setSubscribeUrlHttps(`https://${baseUrl}`);
          setSubscribeUrlWebcal(`webcal://${baseUrl}`);
      }
  }, [userId]);

  // 2. Importierte Kalender laden
  useEffect(() => {
      const loadCalendars = async () => {
          try {
              const savedData = localStorage.getItem('importedCalendars');
              if (savedData) {
                  const calendars = JSON.parse(savedData);
                  setImportedCalendars(calendars);
                  
                  const updatedCalendars = await Promise.all(calendars.map(async (cal) => {
                      const events = await fetchEventsFromUrl(cal.url);
                      return { ...cal, events }; 
                  }));
                  
                  setImportedCalendars(updatedCalendars);
              }
          } catch (e) {
              console.error("Fehler beim Laden der Importe:", e);
          }
      };
      loadCalendars();
  }, []);

  const fetchEventsFromUrl = async (url) => {
      if (!url) return [];
      if (!fetchExternalCalendarEventsCallable) return []; 
      try {
          const result = await fetchExternalCalendarEventsCallable({ calendarUrl: url });
          if (result.data && result.data.success) {
              return result.data.events || [];
          }
      } catch (error) {
          console.error("Fehler beim Abrufen von:", url, error);
      }
      return [];
  };

  const handleAddImport = async () => {
      if (!newImportUrl) return;
      setIsImporting(true);
      const events = await fetchEventsFromUrl(newImportUrl);
      const newCalendar = {
          id: crypto.randomUUID(),
          url: newImportUrl,
          name: newImportName || `Kalender ${importedCalendars.length + 1}`,
          events: events,
          colorIndex: importedCalendars.length % IMPORT_COLORS.length
      };
      const updatedList = [...importedCalendars, newCalendar];
      setImportedCalendars(updatedList);
      localStorage.setItem('importedCalendars', JSON.stringify(updatedList));
      setNewImportUrl('');
      setNewImportName('');
      setIsImporting(false);
  };
  
  const handleRemoveImport = (id) => {
      const updatedList = importedCalendars.filter(c => c.id !== id);
      setImportedCalendars(updatedList);
      localStorage.setItem('importedCalendars', JSON.stringify(updatedList));
  }

  const copyToClipboard = (text) => {
      navigator.clipboard.writeText(text);
      alert("Link kopiert!");
  };
  
  // 3. DATEN KOMBINIEREN: Meetings + Aufgaben + Importe
  const events = useMemo(() => {
    const allMeetings = [...meetings, ...externalMeetings];
    const eventMap = {};

    // A. Meetform Meetings
    allMeetings.forEach(meeting => {
      if (!meeting.date) return;
      const [y, m, d] = meeting.date.split('-').map(Number);
      const safeKey = new Date(y, m - 1, d).toDateString();

      const newEvent = {
        id: meeting.id,
        title: meeting.title || "Unbenannt",
        time: meeting.startTime,
        type: meeting.status === 'completed' ? 'done' : (meeting.isExternal ? 'external' : 'internal'),
        description: `Status: ${meeting.status}`,
        location: meeting.location,
        originalData: meeting,
        isTask: false
      };

      if (!eventMap[safeKey]) eventMap[safeKey] = [];
      eventMap[safeKey].push(newEvent);
    });

    // B. AUFGABEN (NEUE LOGIK)
    if (Array.isArray(tasks)) {
        tasks.forEach(task => {
            // Nur Aufgaben mit Fälligkeitsdatum anzeigen
            if (!task.dueDate) return;
            
            let dObj = null;
            // Versuch 1: Standard YYYY-MM-DD
            if (task.dueDate.includes('-')) {
                 const [y, m, d] = task.dueDate.split('-').map(Number);
                 dObj = new Date(y, m - 1, d);
            } 
            // Versuch 2: Deutsches Format DD.MM.YYYY
            else if (task.dueDate.includes('.')) {
                 const [d, m, y] = task.dueDate.split('.').map(Number);
                 dObj = new Date(y, m - 1, d);
            }
            
            // Wenn Datum ungültig, überspringen
            if (!dObj || isNaN(dObj.getTime())) return;

            const safeKey = dObj.toDateString();
            
            const isCompleted = task.status === 'Erledigt' || task.isCompleted;

            const newEvent = {
                id: task.id,
                title: task.title || "Aufgabe",
                time: 'Fällig', // Platzhalter für Zeit
                type: isCompleted ? 'task-done' : 'task',
                description: `Status: ${task.status}`,
                location: task.project || 'Aufgabe',
                originalData: task,
                isTask: true,
                isCompleted: isCompleted
            };

            if (!eventMap[safeKey]) eventMap[safeKey] = [];
            eventMap[safeKey].push(newEvent);
        });
    }

    // C. Importierte Events
    importedCalendars.forEach(cal => {
        const colorTheme = IMPORT_COLORS[cal.colorIndex || 0];
        (cal.events || []).forEach(imp => {
            if (!imp.start) return;
            const d = new Date(imp.start);
            const safeKey = d.toDateString();
            const timeStr = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

            const newEvent = {
                id: imp.uid || Math.random().toString(),
                title: imp.title || "Externer Termin",
                time: timeStr,
                type: 'imported',
                sourceName: cal.name,
                colorTheme: colorTheme,
                description: imp.description || "Importierter Termin",
                location: imp.location,
                isImported: true,
                isTask: false
            };

            if (!eventMap[safeKey]) eventMap[safeKey] = [];
            eventMap[safeKey].push(newEvent);
        });
    });

    // Sortieren: Tasks ohne Zeit nach unten oder oben, dann nach Zeit
    Object.keys(eventMap).forEach(key => {
        eventMap[key].sort((a, b) => {
            // Tasks (Zeit = 'Fällig') nach unten schieben, wenn gewünscht. 
            // Hier: Sortiere alphabetisch bei gleicher Zeit.
            if (a.time === 'Fällig' && b.time !== 'Fällig') return 1;
            if (a.time !== 'Fällig' && b.time === 'Fällig') return -1;
            return (a.time || '').localeCompare(b.time || '');
        });
    });

    return eventMap;
  }, [meetings, externalMeetings, importedCalendars, tasks]);

  // --- HELPER ---
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay(); 
    const startingDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    const days = [];
    for (let i = 0; i < startingDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const getDaysInWeek = (date) => {
    const curr = new Date(date);
    const day = curr.getDay(); 
    const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
    const week = [];
    const monday = new Date(curr.setDate(diff));
    for (let i = 0; i < 7; i++) {
        const nextDay = new Date(monday);
        nextDay.setDate(monday.getDate() + i);
        week.push(nextDay);
    }
    return week;
  };

  const getAllEventsList = () => {
      const allKeys = Object.keys(events);
      const sortedKeys = allKeys.sort((a, b) => new Date(a) - new Date(b));
      return sortedKeys.map(key => ({ date: new Date(key), items: events[key] })).filter(group => group.date >= new Date().setHours(0,0,0,0));
  };

  const changePeriod = (increment) => {
    const newDate = new Date(currentDate);
    if (view === 'month') newDate.setMonth(newDate.getMonth() + increment);
    else if (view === 'week') newDate.setDate(newDate.getDate() + (increment * 7));
    setCurrentDate(newDate);
  };

  const isSameDay = (d1, d2) => d1 && d2 && d1.toDateString() === d2.toDateString();
  const isToday = (date) => date && isSameDay(date, new Date());
  const getEventsForDate = (date) => (!date ? [] : events[date.toDateString()] || []);

  // --- STYLE HELPER ---
  const getEventColor = (evt) => {
      if (evt.type === 'task') return 'bg-amber-500 dark:bg-amber-500';
      if (evt.type === 'task-done') return 'bg-gray-400 dark:bg-gray-600';
      if (evt.type === 'external') return 'bg-emerald-500 dark:bg-emerald-400';
      if (evt.type === 'imported') return evt.colorTheme?.bg || 'bg-purple-500';
      if (evt.type === 'done') return 'bg-gray-400 dark:bg-slate-500';
      return 'bg-blue-500 dark:bg-blue-400';
  };

  const getEventBgClass = (evt) => {
      if (evt.type === 'task') return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800/30';
      if (evt.type === 'task-done') return 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800/50 dark:text-gray-500 dark:border-gray-700 decoration-line-through';
      if (evt.type === 'imported') return `${evt.colorTheme?.bgLight || 'bg-purple-50'} dark:${evt.colorTheme?.darkBg || 'bg-purple-900/20'} ${evt.colorTheme?.border || 'border-purple-500'}`;
      if (evt.type === 'external') return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border-emerald-200';
      return 'bg-white dark:bg-slate-700/50 border-blue-400';
  };

  const handleEventClick = (event) => {
    if (event.isTask) {
        if (onSelectTask) onSelectTask(event.originalData);
    } else if (!event.isImported) {
        if (onSelectMeeting) onSelectMeeting(event.originalData, 'view');
    }
  };

  // --- RENDER ---
  return (
    <div className="h-full bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 p-4 md:p-6 font-sans overflow-hidden flex flex-col rounded-xl transition-colors duration-300 relative">
      
      {/* TOOLBAR */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                {view === 'list' ? 'Kommende Termine & Aufgaben' : `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
            </h2>
            {view !== 'list' && (
                <div className="flex gap-1 bg-white dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700 shadow-sm">
                    <button onClick={() => changePeriod(-1)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><ChevronLeft size={18} /></button>
                    <button onClick={() => setCurrentDate(new Date())} className="text-xs font-bold px-3 py-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors">Heute</button>
                    <button onClick={() => changePeriod(1)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><ChevronRight size={18} /></button>
                </div>
            )}
          </div>

          <div className="flex gap-2">
              {/* IMPORT BUTTON */}
              <button 
                onClick={() => setShowImportModal(true)} 
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 transition-colors border border-purple-200 dark:border-purple-800"
              >
                  <ImportIcon size={16} /> <span className="hidden md:inline">Importieren</span>
              </button>
              
              {/* ABO BUTTON */}
              <button 
                onClick={() => setShowSubscribeModal(true)} 
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 transition-colors border border-indigo-200 dark:border-indigo-800"
              >
                  <LinkIcon size={16} /> <span className="hidden md:inline">Abonnieren</span>
              </button>

              <div className="flex bg-white dark:bg-slate-800 p-1 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
                  <button onClick={() => setView('month')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${view === 'month' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700'}`}>Monat</button>
                  <button onClick={() => setView('week')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${view === 'week' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700'}`}>Woche</button>
                  <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${view === 'list' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700'}`}>Liste</button>
              </div>
          </div>
      </div>

      {/* --- MODAL: ABO --- */}
      {showSubscribeModal && (
          <div className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-slate-700 animate-fadeIn">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">Kalender exportieren</h3>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">Ihre Meetform-Termine in Outlook, Apple oder Google anzeigen.</p>
                  <div className="mb-4">
                      <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Schnellstart (App öffnen)</label>
                      {subscribeUrlWebcal ? (
                        <a href={subscribeUrlWebcal} className="flex items-center justify-center gap-2 w-full p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-colors"><ExternalLinkIcon size={18} /> In Kalender-App öffnen</a>
                      ) : <div className="text-center text-sm text-gray-400 p-2">Lade Link...</div>}
                  </div>
                  <div className="mb-6">
                      <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Manueller Link (Google)</label>
                      <div className="flex gap-2"><input type="text" readOnly value={subscribeUrlHttps} className="flex-1 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-xs font-mono select-all text-gray-600 dark:text-gray-300" /><button onClick={() => copyToClipboard(subscribeUrlHttps)} className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300 rounded-lg"><CopyIcon size={18} /></button></div>
                  </div>
                  <div className="flex justify-end"><button onClick={() => setShowSubscribeModal(false)} className="px-4 py-2 text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-white font-medium transition-colors">Schließen</button></div>
              </div>
          </div>
      )}

      {/* --- MODAL: IMPORT (JETZT MIT LISTE) --- */}
      {showImportModal && (
          <div className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-slate-700 animate-fadeIn max-h-[80vh] overflow-y-auto">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">Kalender importieren</h3>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
                      Binden Sie externe Kalender (Google, Outlook) per iCal-URL ein.
                  </p>

                  {/* LISTE DER IMPORTIERTEN KALENDER */}
                  {importedCalendars.length > 0 && (
                      <div className="mb-6 space-y-2">
                          <label className="text-xs font-bold text-gray-400 uppercase block">Aktive Importe</label>
                          {importedCalendars.map((cal, idx) => {
                              const theme = IMPORT_COLORS[cal.colorIndex || 0];
                              return (
                                  <div key={cal.id} className="flex items-center justify-between bg-gray-50 dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-slate-700">
                                      <div className="flex items-center gap-2 overflow-hidden">
                                          <div className={`w-3 h-3 rounded-full ${theme.bg}`}></div>
                                          <span className="text-sm font-medium truncate text-gray-700 dark:text-gray-300">{cal.name}</span>
                                      </div>
                                      <button onClick={() => handleRemoveImport(cal.id)} className="text-red-400 hover:text-red-600 p-1"><TrashIcon size={16} /></button>
                                  </div>
                              );
                          })}
                      </div>
                  )}

                  <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                      <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Neuen Kalender hinzufügen</label>
                      <input 
                        type="text" 
                        value={newImportName}
                        onChange={(e) => setNewImportName(e.target.value)}
                        placeholder="Name (z.B. Privat)"
                        className="w-full mb-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                      />
                      <input 
                        type="text" 
                        value={newImportUrl} 
                        onChange={(e) => setNewImportUrl(e.target.value)}
                        placeholder="https://calendar.google.com/..."
                        className="w-full mb-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                      />
                      {isImporting && <p className="text-xs text-purple-500 mb-2 animate-pulse">Lade Daten...</p>}
                      <button 
                        onClick={handleAddImport} 
                        disabled={!newImportUrl || isImporting}
                        className="w-full py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg font-bold transition-colors text-sm"
                      >
                          Hinzufügen
                      </button>
                  </div>
                  
                  <div className="flex justify-end mt-4">
                      <button onClick={() => setShowImportModal(false)} className="px-4 py-2 text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-white font-medium transition-colors text-sm">Schließen</button>
                  </div>
              </div>
          </div>
      )}

      <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden">
        
        {/* MAIN VIEW AREA */}
        <div className={`flex-1 bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-3xl shadow-xl dark:shadow-2xl overflow-hidden flex flex-col ${view === 'list' ? 'lg:w-full' : ''}`}>
          
          {/* VIEW: MONTH */}
          {view === 'month' && (
              <div className="flex-1 p-6 flex flex-col overflow-y-auto">
                <div className="grid grid-cols-7 mb-4">
                    {DAYS.map(day => <div key={day} className="text-center text-gray-400 dark:text-slate-500 text-xs font-semibold uppercase tracking-wider">{day}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-2 flex-1 auto-rows-fr">
                    {getDaysInMonth(currentDate).map((date, index) => {
                        if (!date) return <div key={`empty-${index}`} className="aspect-square" />;
                        const dayEvents = getEventsForDate(date);
                        const isSelected = isSameDay(date, selectedDate);
                        const isCurrentDay = isToday(date);
                        
                        return (
                            <button
                                key={index}
                                onClick={() => setSelectedDate(date)}
                                className={`
                                    relative group rounded-2xl flex flex-col items-center justify-start pt-2 transition-all duration-300 min-h-[60px] md:min-h-[80px] w-full
                                    ${isSelected ? 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg text-white ring-2 ring-blue-400/50 z-10' : 'hover:bg-gray-50 dark:hover:bg-white/5 text-gray-600 dark:text-slate-300 bg-gray-50/50 dark:bg-slate-800/30'}
                                    ${isCurrentDay && !isSelected ? 'border border-blue-500/50 bg-blue-50 dark:bg-blue-500/10' : 'border border-transparent'}
                                `}
                            >
                                <span className={`text-sm md:text-lg font-medium ${isCurrentDay && !isSelected ? 'text-blue-600 dark:text-blue-400' : ''}`}>{date.getDate()}</span>
                                <div className="flex flex-wrap justify-center gap-1 mt-2 max-w-[80%]">
                                    {dayEvents.slice(0, 4).map((evt, i) => (
                                        <div key={i} className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : getEventColor(evt)}`} />
                                    ))}
                                    {dayEvents.length > 4 && <span className="text-[8px] leading-none opacity-50">+</span>}
                                </div>
                            </button>
                        );
                    })}
                </div>
              </div>
          )}

          {/* VIEW: WEEK */}
          {view === 'week' && (
              <div className="flex-1 p-4 overflow-y-auto">
                  <div className="grid grid-cols-7 h-full gap-2 min-w-[600px]">
                      {getDaysInWeek(currentDate).map((date, index) => {
                          const isCurrentDay = isToday(date);
                          const dayEvents = getEventsForDate(date);
                          const isSelected = isSameDay(date, selectedDate);

                          return (
                              <div 
                                key={index} 
                                onClick={() => setSelectedDate(date)}
                                className={`rounded-xl border flex flex-col transition-all cursor-pointer 
                                    ${isSelected ? 'bg-white dark:bg-slate-800 border-blue-500 shadow-md' : 'bg-gray-50/50 dark:bg-slate-800/30 border-gray-200 dark:border-white/5 hover:bg-gray-100 dark:hover:bg-slate-800/50'} 
                                    ${isCurrentDay ? 'ring-2 ring-blue-500/30' : ''}`}
                              >
                                  <div className={`p-2 text-center border-b border-gray-100 dark:border-white/5 ${isCurrentDay ? 'bg-blue-50 dark:bg-blue-500/10' : ''}`}>
                                      <div className="text-xs text-gray-400 dark:text-slate-500 uppercase">{DAYS[index]}</div>
                                      <div className={`text-lg font-bold ${isCurrentDay ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-slate-200'}`}>{date.getDate()}</div>
                                  </div>
                                  <div className="flex-1 p-1 space-y-1 overflow-y-auto custom-scrollbar">
                                      {dayEvents.map(evt => (
                                          <div key={evt.id} className={`text-[10px] p-1.5 rounded border-l-2 shadow-sm truncate hover:brightness-95 transition-colors text-gray-700 dark:text-slate-200 ${getEventBgClass(evt)}`}>
                                              <span className="font-bold block text-gray-900 dark:text-white flex items-center gap-1">
                                                  {evt.isTask ? <CheckSquare size={10} /> : null}
                                                  {evt.time}
                                              </span>
                                              <span className="truncate">{evt.title}</span>
                                          </div>
                                      ))}
                                  </div>
                              </div>
                          );
                      })}
                  </div>
              </div>
          )}

          {/* VIEW: LIST */}
          {view === 'list' && (
              <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
                  {getAllEventsList().length === 0 ? (
                      <div className="text-center text-gray-400 dark:text-slate-500 mt-10">Keine anstehenden Termine oder Aufgaben gefunden.</div>
                  ) : (
                      <div className="space-y-6">
                          {getAllEventsList().map((group, idx) => (
                              <div key={idx} className="relative pl-8 border-l border-gray-200 dark:border-slate-700">
                                  <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 rounded-full bg-blue-500 ring-4 ring-gray-50 dark:ring-slate-900" />
                                  <h3 className="text-lg font-bold text-gray-700 dark:text-slate-300 mb-3 ml-2">
                                      {DAYS[group.date.getDay() === 0 ? 6 : group.date.getDay() - 1]}, {group.date.getDate()}. {MONTHS[group.date.getMonth()]} {group.date.getFullYear()}
                                  </h3>
                                  <div className="grid gap-3 ml-2">
                                      {group.items.map(event => (
                                          <div key={event.id} onClick={() => handleEventClick(event)} className={`flex items-center gap-4 p-4 rounded-xl border hover:shadow-md transition-all cursor-pointer group ${getEventBgClass(event)}`}>
                                              <div className={`font-mono font-bold ${event.isImported ? (event.colorTheme?.text || 'text-purple-600') : (event.isTask ? 'text-amber-600' : 'text-blue-600 dark:text-blue-400')}`}>{event.time}</div>
                                              <div className="flex-1">
                                                  <div className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                                      {event.isTask && <CheckSquare size={16} className="text-amber-500" />}
                                                      <span className={event.type === 'task-done' ? 'line-through opacity-70' : ''}>{event.title}</span>
                                                  </div>
                                                  <div className="text-xs text-gray-500 dark:text-slate-400 flex gap-3 mt-1">
                                                      {event.location && <span className="flex items-center gap-1"><MapPin size={10}/> {event.location}</span>}
                                                      <span>{event.description}</span>
                                                  </div>
                                              </div>
                                              <div className={`text-[10px] uppercase px-2 py-1 rounded font-bold 
                                                ${event.type === 'imported' ? `${event.colorTheme?.bgLight || 'bg-purple-100'} ${event.colorTheme?.text || 'text-purple-700'} dark:${event.colorTheme?.darkBg || 'bg-purple-500/20'} dark:${event.colorTheme?.darkText || 'text-purple-300'}` 
                                                : event.type === 'external' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' 
                                                : event.isTask ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                                                : 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'}`}>
                                                  {event.type === 'done' ? 'Fertig' 
                                                   : event.type === 'task-done' ? 'Erledigt'
                                                   : event.type === 'imported' ? event.sourceName || 'Extern' 
                                                   : event.isTask ? 'Aufgabe' 
                                                   : event.type}
                                              </div>
                                          </div>
                                      ))}
                                  </div>
                              </div>
                          ))}
                      </div>
                  )}
              </div>
          )}
        </div>

        {/* SIDEBAR */}
        {view !== 'list' && (
            <div className="lg:w-[350px] bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-white/10 rounded-3xl p-6 flex flex-col shadow-lg dark:shadow-none">
            <div className="flex justify-between items-start mb-6">
                <div>
                <p className="text-xs text-gray-400 dark:text-slate-400 font-bold uppercase tracking-wider mb-1">Details</p>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                    {DAYS[selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1]}, {selectedDate.getDate()}. {MONTHS[selectedDate.getMonth()]}
                </h3>
                </div>
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 flex items-center justify-center border border-blue-200 dark:border-blue-500/30">
                    <CalendarIcon size={16} />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-1 custom-scrollbar">
                {getEventsForDate(selectedDate).length > 0 ? (
                getEventsForDate(selectedDate).map((event) => (
                    <div 
                        key={event.id} 
                        onClick={() => handleEventClick(event)}
                        className={`group relative pl-4 border-l-2 transition-colors cursor-pointer 
                            ${event.isImported ? `${event.colorTheme?.border.replace('border', 'border') || 'border-purple-500'} hover:brightness-90` 
                            : event.isTask ? 'border-amber-400 hover:border-amber-600'
                            : 'border-gray-300 dark:border-slate-700 hover:border-blue-500'}`}
                    >
                        <div className={`p-3 rounded-lg border border-transparent transition-all shadow-sm ${getEventBgClass(event)}`}>
                            <div className="flex justify-between items-start mb-1">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 
                                    ${event.type==='imported' ? `${event.colorTheme?.bg || 'bg-purple-100'} ${event.colorTheme?.text || 'text-purple-700'} dark:${event.colorTheme?.darkBg || 'bg-purple-500/20'} dark:${event.colorTheme?.darkText || 'text-purple-300'}` 
                                    : event.type==='external'?'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                                    : event.isTask ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                                    :'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300'}`}>
                                    {event.isTask ? <CheckSquare size={10} /> : <Clock size={10} />} 
                                    {event.time}
                                </span>
                            </div>
                            <h4 className={`text-sm font-semibold text-gray-800 dark:text-slate-200 mb-0.5 ${event.type === 'task-done' ? 'line-through opacity-70' : ''}`}>{event.title}</h4>
                            <p className="text-xs text-gray-500 dark:text-slate-500 line-clamp-1">{event.location || (event.isImported ? event.sourceName : (event.isTask ? (event.location || 'Aufgabe') : 'Online Meeting'))}</p>
                        </div>
                    </div>
                ))
                ) : (
                <div className="h-40 flex flex-col items-center justify-center text-gray-400 dark:text-slate-500 opacity-50 border-2 border-dashed border-gray-200 dark:border-slate-800 rounded-xl">
                    <CalendarIcon size={32} className="mb-2" />
                    <p className="text-sm">Keine Termine</p>
                </div>
                )}
            </div>
            </div>
        )}
      </div>
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.05); }
        .dark .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.02); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.1); border-radius: 10px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(0, 0, 0, 0.2); }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.2); }
      `}</style>
    </div>
  );
}